/**
 * Public CRM API client (Phase-1 carservice integration).
 *
 * Talks to the unauthenticated, CORS-friendly endpoints under
 *   https://crm.wisedrive.in/api/public/*
 * No site-key header — these endpoints are public-by-design (rate-limited
 * server-side per IP).
 *
 * Env-configurable:
 *   REACT_APP_PUBLIC_CRM_URL  — base URL (defaults to prod)
 *
 * Endpoints implemented (per CRM handoff doc):
 *   GET    /api/public/service-packages           — list, filterable by brand/fuel/body_class
 *   GET    /api/public/service-packages/{slug}    — single (slug or uuid)
 *   POST   /api/public/service-leads              — create lead, triggers OTP SMS
 *   POST   /api/public/service-leads/{id}/verify-otp
 *   POST   /api/public/service-leads/{id}/resend-otp
 *   GET    /api/public/carservice/stats?city=
 *   GET    /api/public/carservice/cities
 */

import axios from "axios";
import { toErrorMessage } from "./crmApi";

const BASE_URL =
  process.env.REACT_APP_PUBLIC_CRM_URL || "https://crm.wisedrive.in";

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// -- Body class / fuel mapping ----------------------------------------
//
// Our local UI uses snake_case (hatchback / compact_suv) and lowercase fuel
// (petrol / diesel / cng). The CRM API uses Title-Case strings exactly:
//   Hatchback, Sedan, Compact SUV, Mid SUV, Premium
//   Petrol, Diesel, CNG, Electric, Hybrid

export const BODY_CLASS_TO_API = {
  hatchback: "Hatchback",
  sedan: "Sedan",
  compact_suv: "Compact SUV",
  mid_suv: "Mid SUV",
  premium: "Premium",
};

export const BODY_CLASS_FROM_API = {
  Hatchback: "hatchback",
  Sedan: "sedan",
  "Compact SUV": "compact_suv",
  "Mid SUV": "mid_suv",
  Premium: "premium",
};

export const FUEL_TO_API = {
  petrol: "Petrol",
  diesel: "Diesel",
  cng: "CNG",
  electric: "Electric",
  hybrid: "Hybrid",
};

// -- Service Packages -------------------------------------------------

/**
 * Fetches the list of active service packages.
 * Optional filters trigger the server to compute a `quoted_price` per package.
 *
 * @param {{brand?:string, fuel_type?:string, body_class?:string}=} filters
 * @returns {Promise<{
 *   body_classes:string[], fuels:string[],
 *   packages:Array<{
 *     id:string, slug:string, name:string, category:string,
 *     description:string, hero_image_url?:string,
 *     items:Array<{name:string, description:string}>,
 *     pricing_matrix:Object,
 *     starting_price:number, currency:string,
 *     is_active:boolean, sort_order:number,
 *     quoted_price?:number,
 *   }>,
 * }>}
 */
export async function fetchServicePackages(filters = {}) {
  const params = {};
  if (filters.brand) params.brand = filters.brand;
  if (filters.fuel_type) params.fuel_type = filters.fuel_type;
  if (filters.body_class) params.body_class = filters.body_class;
  const { data } = await client.get("/api/public/service-packages", { params });
  return data;
}

/**
 * Returns the active GENERAL-category package (the "general car service")
 * — the one wired to the LP's primary CTA. Falls back to the first package
 * if no GENERAL is found.
 */
export async function fetchGeneralServicePackage(filters = {}) {
  const data = await fetchServicePackages(filters);
  const list = data?.packages || [];
  const general = list.find((p) => p.category === "GENERAL" && p.is_active !== false);
  return general || list[0] || null;
}

/**
 * Fetches a single package by slug or UUID.
 */
export async function fetchServicePackage(slugOrId) {
  const { data } = await client.get(
    `/api/public/service-packages/${encodeURIComponent(slugOrId)}`,
  );
  return data;
}

// -- Service Leads + OTP ----------------------------------------------

/**
 * Fire-and-forget draft-lead capture. Sent the moment the user types a
 * 10-digit mobile (debounced upstream), so even abandoners are visible to
 * sales as a `DRAFT_UNVERIFIED` row in the CRM. Server is idempotent on
 * (mobile, last 30 min) — same `lead_id` returned for repeat calls.
 *
 * Caller stashes the returned `lead_id` and passes it as `draft_lead_id`
 * on the eventual /service-leads call to upgrade in-place (no duplicate).
 *
 * Rate limit: 30/min/IP. Errors should be silently swallowed by the caller —
 * a failed draft must never block the user from continuing.
 */
export async function createServiceLeadDraft(payload) {
  const { data } = await client.post(
    "/api/public/service-leads/draft",
    payload,
  );
  return data;
}

/**
 * Creates a service lead. Server returns a `lead_id` and triggers an SMS OTP
 * to the customer's mobile. Lead remains in `OTP_PENDING` (invisible to sales)
 * until verifyOtp succeeds.
 *
 * @returns {Promise<{
 *   lead_id:string, status:string, package_name:string, quoted_price:number,
 *   otp_required:boolean, otp_resend_in_seconds:number, message:string,
 * }>}
 */
export async function createServiceLead(payload) {
  const { data } = await client.post("/api/public/service-leads", payload);
  return data;
}

/**
 * Verifies the 6-digit OTP for a lead. Idempotent on success.
 * Returns 200 on success, 400 on wrong code, 429 after 5 wrong attempts.
 *
 * `extra` carries attribution we re-assert on verify (city) so the CRM can
 * recover/confirm the serviceable city at the moment the lead is verified —
 * even if it was missing/changed on the initial draft/create.
 */
export async function verifyServiceLeadOtp(leadId, otp, extra = {}) {
  const { data } = await client.post(
    `/api/public/service-leads/${encodeURIComponent(leadId)}/verify-otp`,
    { otp, ...extra },
  );
  return data;
}

/**
 * Generates a fresh OTP and invalidates the previous one.
 */
export async function resendServiceLeadOtp(leadId) {
  const { data } = await client.post(
    `/api/public/service-leads/${encodeURIComponent(leadId)}/resend-otp`,
  );
  return data;
}

// -- Stats + Cities (public, cacheable) -------------------------------

const STATS_TTL_MS = 5 * 60 * 1000;
const _statsCache = new Map();

export async function fetchCarserviceStats(city = "Bengaluru") {
  const key = `stats:${city}`;
  const hit = _statsCache.get(key);
  if (hit && Date.now() - hit.ts < STATS_TTL_MS) return hit.data;
  const { data } = await client.get("/api/public/carservice/stats", {
    params: { city },
  });
  _statsCache.set(key, { ts: Date.now(), data });
  return data;
}

export async function fetchCarserviceCities() {
  const { data } = await client.get("/api/public/carservice/cities");
  return data;
}

// -- Re-export error helper -------------------------------------------
export { toErrorMessage };

const api = {
  fetchServicePackages,
  fetchGeneralServicePackage,
  fetchServicePackage,
  createServiceLeadDraft,
  createServiceLead,
  verifyServiceLeadOtp,
  resendServiceLeadOtp,
  fetchCarserviceStats,
  fetchCarserviceCities,
  BODY_CLASS_TO_API,
  BODY_CLASS_FROM_API,
  FUEL_TO_API,
};

export default api;
